import { useEffect, useId, useLayoutEffect, useRef, useState, type FormEvent } from "react";
import { Button } from "../../components/Button";
import { SelectPopover } from "../../components/SelectPopover";
import { TextInput } from "../../components/TextInput";
import { ModalCloseButton } from "../../components/ModalCloseButton";
import { US_STATES } from "../../constants/usStates";
import { cycleTabFocus } from "../../utils/focus-trap";
import {
  TRUSTED_CONTACT_TYPES,
  type TrustedContact,
  type TrustedContactInput,
  type TrustedContactType,
} from "../../types/trusted-contacts";
import { CONTACT_TYPE_LABELS, contactTypeFromLabel, contactTypeLabel } from "./contactTypes";
import "./TrustedContactModal.css";

interface TrustedContactModalProps {
  isOpen: boolean;
  /** The contact being edited, or null when adding a new one. */
  contact: TrustedContact | null;
  onClose: () => void;
  onSave: (input: TrustedContactInput) => Promise<void>;
}

const TYPE_OPTIONS = TRUSTED_CONTACT_TYPES.map((t) => CONTACT_TYPE_LABELS[t]);

const DEFAULT_TYPE: TrustedContactType = "CollegeCoach";

const blank = (): TrustedContactInput => ({
  name: "",
  organizationName: "",
  state: "",
  contactType: DEFAULT_TYPE,
  phone: "",
  email: "",
  notes: "",
});

const fromContact = (contact: TrustedContact): TrustedContactInput => ({
  name: contact.name,
  organizationName: contact.organizationName ?? "",
  state: contact.state ?? "",
  contactType: contact.contactType,
  phone: contact.phone ?? "",
  email: contact.email ?? "",
  notes: contact.notes ?? "",
});

/**
 * Add or edit one directory contact.
 *
 * One modal for both because the fields are identical and a separate "edit" copy is how the two
 * drift. `contact` decides the title, the button copy and the starting values.
 */
export const TrustedContactModal = ({
  isOpen,
  contact,
  onClose,
  onSave,
}: TrustedContactModalProps) => {
  const titleId = useId();
  const notesId = useId();
  const typeId = useId();
  const stateId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  /** Whatever had focus when the dialog opened, so closing puts it back. */
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const [form, setForm] = useState<TrustedContactInput>(blank);
  const [error, setError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /*
    Reseeded every time the modal opens, and keyed on the contact so opening Edit on a second row
    without closing in between still refills the fields. A lazy `useState` initializer would only
    ever read the first contact the component was mounted with.
  */
  useLayoutEffect(() => {
    if (!isOpen) return;
    setForm(contact ? fromContact(contact) : blank());
    setError(null);
    setNameError(null);
    setSaving(false);
    // Moved by hand rather than with `autoFocus`: opening a dialog has to take focus off the
    // trigger behind it, and the attribute also fires on every render React decides to remount.
    // Layout effect so it lands before paint rather than a frame after it.
    nameRef.current?.focus();
  }, [isOpen, contact]);

  /*
    Focus restore and a scroll lock, matching ConfirmModal. Without the restore, closing drops
    focus to <body> and a keyboard user Tabbing from row 8's Edit button restarts at the top of
    the document.
  */
  useEffect(() => {
    if (!isOpen) return;

    returnFocusRef.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
      const target = returnFocusRef.current;
      if (target?.isConnected) target.focus();
    };
  }, [isOpen]);

  // Escape to close, plus a focus trap so Tab cannot reach the page behind an aria-modal dialog.
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const dialog = dialogRef.current;
      if (!dialog) return;

      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }

      if (e.key === "Tab") cycleTabFocus(dialog, e);
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const update = <K extends keyof TrustedContactInput>(
    key: K,
    value: TrustedContactInput[K]
  ) => setForm((current) => ({ ...current, [key]: value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const name = form.name.trim();
    if (!name) {
      // On the field, not in the banner: TextInput wires aria-invalid and aria-describedby, so a
      // screen reader user is told which control is wrong rather than that something is.
      setNameError("Enter a name.");
      return;
    }
    setNameError(null);

    // Mirrors the API's rule rather than trusting it to be the only check: a contact nobody can
    // reach is a row that only makes the directory longer, and saying so here saves a round trip.
    if (!form.phone?.trim() && !form.email?.trim()) {
      setError("Enter a phone number or an email address.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onSave({
        name,
        organizationName: form.organizationName?.trim() || null,
        state: form.state?.trim() || null,
        contactType: form.contactType,
        phone: form.phone?.trim() || null,
        email: form.email?.trim() || null,
        notes: form.notes?.trim() || null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the contact.");
      setSaving(false);
    }
  };

  return (
    <div className="trusted-contact-modal-overlay">
      <div
        className="trusted-contact-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
      >
        <div className="trusted-contact-modal__header">
          <h2 className="trusted-contact-modal__title" id={titleId}>
            {contact ? "Edit contact" : "Add contact"}
          </h2>
          <ModalCloseButton onClick={onClose} />
        </div>

        <form className="trusted-contact-modal__body" onSubmit={handleSubmit}>
          {error && (
            <div className="trusted-contact-modal__error" role="alert">
              {error}
            </div>
          )}

          {/*
            No `required` attribute: the browser's own bubble would preempt the validation below,
            which is what carries the message into the field's aria-describedby.
          */}
          <TextInput
            label="Name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Mike Collins"
            ref={nameRef}
            error={nameError ?? undefined}
          />

          <TextInput
            label={<>Organization / team <span className="trusted-contact-modal__optional">(optional)</span></>}
            value={form.organizationName ?? ""}
            onChange={(e) => update("organizationName", e.target.value)}
            placeholder="Chipola College"
          />

          <div className="trusted-contact-modal__field">
            <label className="trusted-contact-modal__label" htmlFor={stateId}>
              State <span className="trusted-contact-modal__optional">(optional)</span>
            </label>
            {/*
              clearable (the default): unlike contact type, a state is not a required choice, so
              the menu's own reset row is the way back to blank rather than a maintained sentinel.
            */}
            <SelectPopover
              id={stateId}
              options={US_STATES}
              value={form.state ?? ""}
              onChange={(value) => update("state", value || null)}
              placeholder="Choose a state"
              fullWidth
            />
          </div>

          <div className="trusted-contact-modal__field">
            <label className="trusted-contact-modal__label" htmlFor={typeId}>
              Contact type
            </label>
            <SelectPopover
              id={typeId}
              options={TYPE_OPTIONS}
              value={contactTypeLabel(form.contactType)}
              onChange={(label) => {
                const type = contactTypeFromLabel(label);
                if (type) update("contactType", type);
              }}
              placeholder="Choose a type"
              clearable={false}
              fullWidth
            />
          </div>

          <TextInput
            label="Phone"
            type="tel"
            value={form.phone ?? ""}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="(850) 555-0132"
          />

          <TextInput
            label="Email"
            type="email"
            value={form.email ?? ""}
            onChange={(e) => update("email", e.target.value)}
            placeholder="mcollins@chipola.edu"
          />

          <div className="trusted-contact-modal__field">
            <label className="trusted-contact-modal__label" htmlFor={notesId}>
              Notes <span className="trusted-contact-modal__optional">(optional)</span>
            </label>
            <textarea
              id={notesId}
              className="trusted-contact-modal__textarea"
              value={form.notes ?? ""}
              onChange={(e) => update("notes", e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Great contact for RHPs"
            />
          </div>

          <div className="trusted-contact-modal__actions">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={saving}>
              {contact ? "Save changes" : "Add contact"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
